import { useState, type ReactNode } from 'react';
import type { NextQuestion, OnboardingQuestion } from '../../lib/entities/onboarding';

/**
 * The in-modal predicate-question loop — the same yes/no findings loop as onboarding, run against an
 * employee or case after it's created. Starts from an already-fetched first question (so the caller can
 * skip mounting it entirely when there are none), and chains via `submitAnswer` until the server says done.
 *
 * EVERY predicate MUST be answered yes or no. There is deliberately NO skip: an unanswered predicate is
 * absent, and the resolver reads an absent predicate as FALSE, which would silently show the wrong
 * requirement tier. So the loop offers only "Continue", disabled until an answer is chosen, and the
 * caller must keep the modal open (non-dismissable) until `onDone` fires. See COMPLIANCE-MODEL.md.
 *
 * Predicate questions are boolean; the other kinds are handled defensively.
 */
export default function PredicateQuestionLoop({
  lead,
  initialQuestion,
  submitAnswer,
  onDone,
}: {
  lead: ReactNode;
  initialQuestion: OnboardingQuestion;
  submitAnswer: (key: string, value: string) => Promise<NextQuestion>;
  onDone: () => void;
}) {
  const [question, setQuestion] = useState<OnboardingQuestion>(initialQuestion);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canContinue = answer.trim().length > 0 && !submitting;

  async function onContinue() {
    if (!canContinue) return;
    setSubmitting(true);
    setError(null);
    try {
      const next = await submitAnswer(question.key, answer.trim());
      if (next.done || !next.question) {
        onDone();
        return;
      }
      setQuestion(next.question);
      setAnswer('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your answer. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <p className="emp-modal__sub" style={{ marginTop: 0 }}>{lead}</p>
      <h2 className="modal__title" style={{ marginTop: 6 }}>{question.prompt}</h2>
      {question.help && <p className="emp-modal__sub">{question.help}</p>}

      <div style={{ margin: '14px 0 4px' }}>{renderInput(question, answer, setAnswer)}</div>

      {error && (
        <p style={{ margin: 0, color: 'var(--wine)', fontSize: '13.5px' }} role="alert">
          {error}
        </p>
      )}

      <div className="emp-modal__foot" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-primary btn-xs" onClick={onContinue} disabled={!canContinue}>
          {submitting ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </>
  );
}

function toggleBtn(active: boolean): string {
  return `btn btn-sm ${active ? 'btn-primary' : 'btn-secondary'}`;
}

function renderInput(
  question: OnboardingQuestion,
  answer: string,
  setAnswer: (v: string) => void,
): ReactNode {
  switch (question.kind) {
    case 'boolean':
      return (
        <div style={{ display: 'flex', gap: 8 }} role="group" aria-label={question.prompt}>
          <button type="button" className={toggleBtn(answer === 'true')} onClick={() => setAnswer('true')}>
            Yes
          </button>
          <button type="button" className={toggleBtn(answer === 'false')} onClick={() => setAnswer('false')}>
            No
          </button>
        </div>
      );
    case 'choice':
      return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(question.options ?? []).map((o) => (
            <button key={o.value} type="button" className={toggleBtn(answer === o.value)} onClick={() => setAnswer(o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      );
    case 'longText':
      return (
        <textarea
          className="input"
          rows={3}
          style={{ resize: 'vertical' }}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
        />
      );
    case 'number':
      return (
        <input className="input" type="number" value={answer} onChange={(e) => setAnswer(e.target.value)} />
      );
    default:
      return <input className="input" type="text" value={answer} onChange={(e) => setAnswer(e.target.value)} />;
  }
}
